export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function normalizeViewport(viewport, durationMs) {
  const duration = Math.max(1, Number(durationMs) || 1);
  const zoom = clamp(Number(viewport?.zoom) || 1, 1, 64);
  const visibleMs = duration / zoom;
  const startMs = clamp(Number(viewport?.start_ms) || 0, 0, Math.max(0, duration - visibleMs));
  return { zoom, start_ms: startMs, visible_ms: visibleMs, end_ms: startMs + visibleMs };
}

export function xToTime(x, width, viewport, durationMs) {
  const view = normalizeViewport(viewport, durationMs);
  return clamp(view.start_ms + clamp(x / Math.max(1, width), 0, 1) * view.visible_ms, 0, durationMs);
}

export function timeToX(timeMs, width, viewport, durationMs) {
  const view = normalizeViewport(viewport, durationMs);
  return ((timeMs - view.start_ms) / view.visible_ms) * width;
}

export function zoomAt(viewport, durationMs, factor, anchorRatio = 0.5) {
  const before = normalizeViewport(viewport, durationMs);
  const ratio = clamp(anchorRatio, 0, 1);
  const anchorTime = before.start_ms + before.visible_ms * ratio;
  const zoom = clamp(before.zoom * factor, 1, 64);
  const visibleMs = durationMs / zoom;
  return normalizeViewport({ zoom, start_ms: anchorTime - visibleMs * ratio }, durationMs);
}

export function activeLyricIndex(lines, timeMs) {
  if (!Array.isArray(lines) || !lines.length) return -1;
  let low = 0;
  let high = lines.length - 1;
  let match = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (Number(lines[middle].time_ms) <= timeMs) {
      match = middle;
      low = middle + 1;
    } else high = middle - 1;
  }
  return match;
}

export function paragraphBounds(lines, index, gapMs = 8800) {
  if (!Array.isArray(lines) || !lines[index]) return null;
  let start = index;
  let end = index;
  while (start > 0 && lines[start].time_ms - lines[start - 1].time_ms < gapMs) start -= 1;
  while (end + 1 < lines.length && lines[end + 1].time_ms - lines[end].time_ms < gapMs) end += 1;
  return { start, end };
}

export function assignAnnotationLanes(annotations, gap = 4) {
  if (!Array.isArray(annotations) || !annotations.length) return [];
  const sorted = annotations
    .map((annotation, order) => {
      const start = Math.min(Number(annotation.start) || 0, Number(annotation.end) || 0);
      const end = Math.max(start + 1, Number(annotation.start) || 0, Number(annotation.end) || 0);
      return { ...annotation, start, end, _order: order };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end || a._order - b._order);
  const laneEnds = [];
  return sorted.map((annotation) => {
    let lane = laneEnds.findIndex((laneEnd) => annotation.start >= laneEnd + gap);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = annotation.end;
    const { _order, ...result } = annotation;
    return { ...result, lane };
  });
}

export function shouldUsePointMarker(text, maximumCharacters = 4) {
  const characters = Array.from(String(text || "").replace(/\s+/g, ""));
  return characters.length > 0 && characters.length <= maximumCharacters;
}

export function shouldDrawAnnotationStem(hasRange, startX, endX, minimumRangePixels = 3) {
  return !hasRange || Math.abs(Number(endX) - Number(startX)) <= minimumRangePixels;
}

export function isGlobalComment(comment) {
  return comment?.start_ms === null || comment?.start_ms === undefined;
}

export function sortCommentsByAudioTime(comments) {
  return [...(comments || [])].sort((a, b) => {
    const aGlobal = isGlobalComment(a);
    const bGlobal = isGlobalComment(b);
    if (aGlobal !== bGlobal) return aGlobal ? -1 : 1;
    if (!aGlobal && Number(a.start_ms) !== Number(b.start_ms)) return Number(a.start_ms) - Number(b.start_ms);
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });
}

export function sortProjectsForWorkbench(projects) {
  return [...(projects || [])].sort((first, second) => {
    const deliveryOrder = Number(Boolean(first?.is_delivered)) - Number(Boolean(second?.is_delivered));
    if (deliveryOrder) return deliveryOrder;
    const priorityOrder = Number(Boolean(second?.is_priority)) - Number(Boolean(first?.is_priority));
    if (priorityOrder) return priorityOrder;
    return String(second?.updated_at || "").localeCompare(String(first?.updated_at || ""));
  });
}

export function commentsMissingReason(comments, authorId) {
  const all = comments || [];
  const repliedIds = new Set(all.filter((comment) => comment.parent_id && comment.author_id === authorId).map((comment) => comment.parent_id));
  return sortCommentsByAudioTime(all.filter((comment) => !comment.parent_id && !comment.resolved && !repliedIds.has(comment.id)));
}

export function comparisonVersions(versions, currentVersionId) {
  const all = Array.isArray(versions) ? versions : [];
  const current = all.find((version) => version.id === currentVersionId);
  if (!current) return [];
  return all
    .filter((version) => Number(version.version_number) < Number(current.version_number))
    .sort((a, b) => Number(b.version_number) - Number(a.version_number));
}

export function waveformChannels(waveform) {
  const declared = Array.isArray(waveform?.channels)
    ? waveform.channels
      .map((channel, index) => ({
        label: String(channel?.label || (index === 0 ? "L" : "R")),
        peaks: Array.isArray(channel?.peaks) && channel.peaks.length ? channel.peaks : [[0, 0]],
      }))
      .slice(0, 2)
    : [];
  if (declared.length) return declared;
  return [{ label: "M", peaks: Array.isArray(waveform?.peaks) && waveform.peaks.length ? waveform.peaks : [[0, 0]] }];
}

export function engineerVisibleComments(comments, currentVersionId) {
  const all = Array.isArray(comments) ? comments : [];
  const visibleParentIds = new Set(
    all
      .filter((comment) => !comment.parent_id)
      .filter((comment) => !comment.resolved || comment.version_id === currentVersionId)
      .map((comment) => comment.id),
  );
  return all.filter((comment) => comment.parent_id
    ? visibleParentIds.has(comment.parent_id)
    : visibleParentIds.has(comment.id));
}

export function workflowStatusLabel(project, version) {
  if (!version) return "等待首次上传";
  const number = Math.max(1, Number(version.version_number) || 1);
  const versionLabel = number === 1 ? "第一版" : `第${number}版`;
  const status = version.status || project?.status;
  if (status === "approved") return `${versionLabel}已定稿，可交付`;
  if (status === "in_review") return number === 1 ? "第一版待审阅" : `${versionLabel}已修改上传，待审阅`;
  if (status === "changes_requested") {
    return `第${number}次批注待修改${project?.has_review_supplement ? " · 有补充" : ""}`;
  }
  if (status === "uploaded" || status === "processing") {
    return number === 1 ? "第一版已上传，待提交" : `${versionLabel}已修改上传，待提交`;
  }
  if (status === "failed") return `${versionLabel}处理失败`;
  return project?.status || status || "待处理";
}

export function isPendingProject(project) {
  return ["draft", "in_review", "changes_requested"].includes(project?.status);
}

export function isReviewSupplementOpen(project, version) {
  return Boolean(
    project
    && version
    && version.status === "changes_requested"
    && project.supplementable_version_id === version.id,
  );
}
